// Which of a form's fields are actually being asked, given what has been answered.
//
// A form that presents all 51 of its fields at once is not the same form: an agent mid
// call reads past three quarters of a screen that does not apply. 9 of the desk's 12
// forms use conditions, 53 in all, and the Integrations form alone has 15.
//
// NESTING IS THE PART THAT IS EASY TO GET WRONG. A field revealed by one condition can be
// the parent of another, so "does this condition apply" depends on whether its own parent
// is visible yet. Evaluating the list once, in order, gets that wrong whenever a child
// condition is listed before its parent's — and the order is the desk's, not ours. So the
// visible set is computed to a FIXPOINT: keep applying conditions until a pass reveals
// nothing new. The loop is bounded by the number of conditions, because each pass either
// adds a field or stops.
//
// No JSX here on purpose, so `node --test` can import it.

/**
 * Does a parent field's current answer match what the condition is waiting for?
 *
 * Both sides are compared as text. UI state holds strings (a checkbox is 'true'/'false' or
 * a boolean depending on the control), and `ticket_form_conditions.parent_value` is
 * nvarchar because the parent may be a select, a checkbox or a text field. A multiselect
 * parent matches when the wanted value is ANY of the chosen ones, which is the only
 * reading that makes sense: "when they picked DoorDash" is true of a list containing it.
 */
export function parentMatches(answer, wanted) {
  const want = String(wanted ?? '');
  if (Array.isArray(answer)) return answer.map(String).includes(want);
  if (answer === null || answer === undefined) return false;
  if (typeof answer === 'boolean') return String(answer) === want;
  return String(answer) === want;
}

/**
 * @param {Array} fields      the form's fields, in order
 * @param {Array} conditions  `{ parent_key, value, children: [{ key, is_required }] }`
 * @param {object} values     what the agent has answered so far, keyed by field key
 * @returns {{visible: Array, requiredKeys: Set<string>}}
 *
 * A field named as a CHILD of any condition starts hidden; everything else is always
 * shown. That is Zendesk's rule and the only one that makes the feature worth having —
 * if conditional fields were visible by default, a condition could only ever hide things,
 * and the agent would watch the form shrink as they typed.
 */
export function applyFormConditions(fields, conditions, values) {
  const list = Array.isArray(conditions) ? conditions : [];
  const conditional = new Set();
  for (const condition of list) {
    for (const child of condition.children ?? []) conditional.add(child.key);
  }

  const visibleKeys = new Set(
    (fields ?? []).map((field) => field.key).filter((key) => !conditional.has(key)),
  );
  const requiredKeys = new Set();

  // The fixpoint. At most one pass per condition is ever needed: each pass that changes
  // anything reveals at least one field, and there are only so many to reveal.
  for (let pass = 0; pass <= list.length; pass += 1) {
    let changed = false;
    for (const condition of list) {
      // A condition whose own parent is still hidden has not been asked yet, so it cannot
      // be satisfied — this is what makes nesting work regardless of the order the
      // conditions arrive in.
      if (!visibleKeys.has(condition.parent_key)) continue;
      if (!parentMatches(values?.[condition.parent_key], condition.value)) continue;

      for (const child of condition.children ?? []) {
        if (!visibleKeys.has(child.key)) {
          visibleKeys.add(child.key);
          changed = true;
        }
        if (child.is_required) requiredKeys.add(child.key);
      }
    }
    if (!changed) break;
  }

  return {
    visible: (fields ?? []).filter((field) => visibleKeys.has(field.key)),
    requiredKeys,
  };
}

/**
 * The fields to validate and submit: the form's own `is_required`, plus anything a
 * satisfied condition made mandatory.
 *
 * `is_required` is merged rather than replaced, because the two mean different things.
 * The form's flag is "always needed on this form"; a condition's is "needed ONCE THIS
 * CONDITION IS MET", which is stronger and only applies while the field is showing.
 */
export function visibleFieldsWithRequirement(fields, conditions, values) {
  const { visible, requiredKeys } = applyFormConditions(fields, conditions, values);
  return visible.map((field) => (requiredKeys.has(field.key)
    ? { ...field, is_required: true }
    : field));
}

/**
 * The answers worth submitting: only those to questions the form is actually asking.
 *
 * A value left behind by a field that has since been hidden is an answer to a question
 * nobody is being asked any more — Zendesk drops those too. Keeping it would file, say, a
 * DoorDash go-live date against a ticket whose integration was changed to something else,
 * and nothing on the screen would show it.
 */
export function valuesForVisibleFields(fields, conditions, values) {
  const { visible } = applyFormConditions(fields, conditions, values);
  const shown = new Set(visible.map((field) => field.key));
  const out = {};
  for (const [key, value] of Object.entries(values ?? {})) {
    if (shown.has(key)) out[key] = value;
  }
  return out;
}
